/** Money for the browser — deliberately dependency-free.
 *
 *  Stands alone (no React, no API client) so `scripts/check-money.mjs` can
 *  exercise the real functions in a plain Node process. Separators and symbol
 *  position follow the READER's locale; the currency follows the operator /
 *  facility. The same euro figure is €1,234.56 to an Irish reader and
 *  1.234,56 € to a German one — deriving that from the currency alone is
 *  wrong for exactly the markets we are entering.
 *
 *  Before this, ~47 component files each did their own
 *  ``$${(cents/100).toLocaleString()}`` — hardcoding USD and the `$`.
 *  `scripts/check-money.mjs` keeps them from coming back.
 */

// How many decimal places a currency has, asked of the platform rather than
// kept in a list. The yen has none, so ¥1,000 is stored as 1000 and the `/100`
// right for dollars renders it as ¥10 — two orders of magnitude, silently.
// Intl already knows this for every currency it supports.
const _EXP = new Map<string, number>();
export function currencyExponent(currency = "USD"): number {
  const cur = String(currency || "USD").toUpperCase();
  const cached = _EXP.get(cur);
  if (cached !== undefined) return cached;
  let exp = 2;
  try {
    const d = new Intl.NumberFormat("en", { style: "currency", currency: cur }).resolvedOptions()
      .maximumFractionDigits;
    if (typeof d === "number") exp = d;
  } catch {
    exp = 2; // an unknown code: behave like the common case
  }
  _EXP.set(cur, exp);
  return exp;
}

/** An amount in the currency's smallest unit, written the way its reader
 *  expects. Both args default to the US so every existing caller is
 *  unchanged. */
export function fmtMoney(minorUnits: number | null | undefined, currency = "USD", locale = "en-US"): string {
  if (minorUnits == null) return "—";
  const cur = String(currency || "USD").toUpperCase();
  const amount = minorUnits / 10 ** currencyExponent(cur);
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: cur }).format(amount);
  } catch {
    // An unknown ISO code renders as itself rather than being relabelled —
    // "1,234.56 ZZZ" is honest where a guessed symbol is misleading.
    return `${new Intl.NumberFormat(locale).format(amount)} ${cur}`;
  }
}

/** Minor units → the number a person types into a price box, and back. The
 *  only two places a raw `/100` or `*100` is correct — and correct because the
 *  exponent comes from the currency, not an assumption about cents. */
export function toMajor(minorUnits: number | null | undefined | "", currency = "USD"): string {
  if (minorUnits == null || minorUnits === "") return "";
  const exp = currencyExponent(currency);
  const v = (minorUnits as number) / 10 ** exp;
  return exp === 0 ? String(Math.round(v)) : v.toFixed(exp);
}

export function fromMajor(typed: string | number, currency = "USD"): number | null {
  const n = Number(String(typed).replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10 ** currencyExponent(currency));
}

/** Just the symbol, for a field adornment beside a bare number. Derived from a
 *  formatted sample so it agrees with `fmtMoney` by construction. */
export function currencySymbol(currency = "USD", locale = "en-US"): string {
  const cur = String(currency || "USD").toUpperCase();
  try {
    const parts = new Intl.NumberFormat(locale, { style: "currency", currency: cur }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value || cur;
  } catch {
    return cur;
  }
}

/** Whole units, for a price ladder that shows "99" rather than "99.00". */
export function wholeUnits(minorUnits: number | null | undefined, currency = "USD"): number {
  return Math.round((minorUnits || 0) / 10 ** currencyExponent(currency));
}

/** The tax a NestPlot SUBSCRIPTION attracts in the market that bills in this
 *  currency. One currency → one word, which keeps this a lookup rather than a
 *  country table. Mirrors the API's PricingLadderResponse.tax_label. */
export function subscriptionTaxLabel(currency = "USD"): string {
  switch (String(currency || "USD").toUpperCase()) {
    case "EUR":
    case "GBP":
      return "VAT";
    case "CAD":
      return "GST/HST";
    case "AUD":
    case "NZD":
      return "GST";
    default:
      return "sales tax";
  }
}

/** Can the buyer make this a reverse-charged supply by giving us a VAT number?
 *  True for the EU and the UK, where a non-EU/non-UK supplier of B2B digital
 *  services charges nothing and the business self-accounts — so the honest
 *  headline for most of Europe is that they pay the published price and no VAT
 *  at all. */
export function reverseChargeAvailable(currency = "USD"): boolean {
  const cur = String(currency || "USD").toUpperCase();
  return cur === "EUR" || cur === "GBP";
}
